const EXCLUDE_OTHER_ATTR = "data-exclude-other"
const INCLUDE_OTHER_ATTR = "data-include-other"
const UNCHECK_OTHER_ATTR = "data-uncheck-other"

export default (commonView) =>
  ({ nextStepPath, formFields, redirectIfWeightGainUrl, isSkipStep, maxOptions }) => ({
    ...commonView({
      nextStepPath,
      formFields,
      redirectIfWeightGainUrl,
      isSkipStep,
    }),

    initContentHidden: true,

    isDisabled(input, fieldName) {
      const relatedInputs = input.closest("form").querySelectorAll(`input[name="${input.name}"]`)
      const excludingInput = Array.from(relatedInputs).find((el) =>
        el.hasAttribute(EXCLUDE_OTHER_ATTR),
      )
      const uncheckingInput = Array.from(relatedInputs).find((el) =>
        el.hasAttribute(UNCHECK_OTHER_ATTR),
      )
      const isCurrentExcluding = excludingInput === input
      const isCurrentUnchecking = uncheckingInput === input

      // Excluding or unchecking inputs are always enabled
      if (isCurrentExcluding || isCurrentUnchecking) return false

      // If excluding is selected, that the current should be disabled
      const isExcludingSelected =
        excludingInput && this.formValues[fieldName]?.includes(excludingInput.value)

      // Validate if max amount of options is already selected
      const isMaxOptionsSelected =
        !input.checked && this.formValues[fieldName]?.length >= maxOptions

      return isExcludingSelected || isMaxOptionsSelected
    },

    getFormValues(form) {
      const formData = new FormData(form)
      const newFormValues = {}

      for (const [name, value] of formData.entries()) {
        if (newFormValues[name]) {
          // For multiple fields, the FormData API provides names duplication in entries
          if (Array.isArray(newFormValues[name])) {
            newFormValues[name].push(value)
          } else {
            newFormValues[name] = [newFormValues[name], value]
          }
        } else {
          newFormValues[name] = [value]
        }
      }

      return newFormValues
    },

    updateOtherInputs({ form, input, checked, disabled }) {
      const relatedInputs = form.querySelectorAll(`input[name="${input.name}"]`)
      const otherInputs = Array.from(relatedInputs).filter((el) => el !== input)

      otherInputs.forEach((otherInput) => {
        otherInput.checked = checked
        otherInput.disabled = disabled
      })
    },

    updateSpecificInput({ form, input, attr, checked }) {
      const relatedInputs = form.querySelectorAll(`input[name="${input.name}"]`)
      const specificInput = Array.from(relatedInputs).find((el) => el.hasAttribute(attr))

      if (specificInput && specificInput.checked !== checked) {
        specificInput.checked = checked
      }
    },

    handleFormChange($event) {
      const { currentTarget: form, target: input } = $event

      // Excluding input selected:
      // Uncheck and disable other inputs
      if (input.hasAttribute(EXCLUDE_OTHER_ATTR)) {
        this.updateOtherInputs({
          form,
          input,
          checked: false,
          disabled: input.checked,
        })
      }

      // Unchecking input selected:
      // Uncheck other inputs if unchecking one has been selected
      if (input.hasAttribute(UNCHECK_OTHER_ATTR)) {
        this.updateOtherInputs({
          form,
          input,
          checked: false,
        })
      }

      // Including other input selected:
      // Check other inputs except excluding one
      if (input.hasAttribute(INCLUDE_OTHER_ATTR)) {
        this.updateOtherInputs({
          form,
          input,
          checked: true,
        })

        // Uncheck unchecking input if it was previously selected
        this.updateSpecificInput({
          form,
          input,
          attr: UNCHECK_OTHER_ATTR,
          checked: false,
        })
      }

      // Regular input selected:
      if (
        !input.hasAttribute(EXCLUDE_OTHER_ATTR) &&
        !input.hasAttribute(UNCHECK_OTHER_ATTR) &&
        !input.hasAttribute(INCLUDE_OTHER_ATTR)
      ) {
        // Uncheck unchecking input if it was previously selected
        this.updateSpecificInput({
          form,
          input,
          attr: UNCHECK_OTHER_ATTR,
          checked: false,
        })

        // Uncheck including input if it was previously selected
        this.updateSpecificInput({
          form,
          input,
          attr: INCLUDE_OTHER_ATTR,
          checked: false,
        })

        // Max options validation to disable non-selected others if max achieved and vise versa
        if (maxOptions) {
          const newFormValues = this.getFormValues(form)
          const isMaxOptionsSelected = newFormValues[input.name]?.length >= maxOptions

          const relatedInputs = form.querySelectorAll(`input[name="${input.name}"]`)
          const otherInputs = Array.from(relatedInputs).filter((otherInput) => {
            return (
              otherInput !== input &&
              !otherInput.hasAttribute(EXCLUDE_OTHER_ATTR) &&
              !otherInput.hasAttribute(UNCHECK_OTHER_ATTR)
            )
          })

          otherInputs.forEach((otherInput) => {
            otherInput.disabled = !otherInput.checked && isMaxOptionsSelected
          })
        }
      }

      const newFormValues = this.getFormValues(form)
      this.setSubmitAllowed(newFormValues, formFields)
    },

    submitForm({ currentTarget: form }) {
      const newFormValues = this.getFormValues(form)

      this.formValues = {
        ...this.formValues,
        ...newFormValues,
      }

      this.submit()
    },
  })
